import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, message, Tooltip } from 'antd';
import styles from './s.module.less';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import CommonTable from 'components/CommonTable';
import AddModal from './components/AddModal';
import { duplicateClosedFunnel, getClosedFunnelLink, getClosedFunnelList, updateClisedFunnelStatus } from 'api/operation/closedFunnel';
import { ICloseFunnelRow, IGetClosedFunnelListInput } from 'types/operation';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import debounce from 'lodash/debounce';
import { PATH } from 'constants/path';
import { useNavigate } from 'react-router-dom';
import dayjs from 'utils/dayjs';
import capitalize from 'lodash/capitalize';

const ClosedFunnel = () => {
    const navigate = useNavigate();
    const [pendingNameFilter, setPendingNameFilter] = useState('');
    const [conditionsSetting] = useGetSettingData(EnumFields.HELLO_KLARITY_CONDITION);
    const [statesSetting] = useGetSettingData(EnumFields.STATE);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [currentLoadingRowId, setCurrentLoadingRowId] = useState<number | null>(null);
    const [currentLoadingRowIdStatus, setCurrentLoadingRowIdStatus] = useState<number | null>(null);
    const searchParams = useRef<IGetClosedFunnelListInput>({
        current: 1,
        size: 10,
        name: '',
        states: null,
        type: 'CONDITION',
        status: null,
        typeValues: null,
        orderType: 'DESC',
    });
    const conditionOptions = useMemo(() => {
        return conditionsSetting ? conditionsSetting.map((item: any) => ({
            label: item.content,
            value: item.content,
        })) : [];
    }, [conditionsSetting]);

    const conditionFilterOptions = useMemo(() => {
        return conditionsSetting ? conditionsSetting.map((item: any) => ({
            text: item.content,
            value: item.content,
        })) : [];
    }, [conditionsSetting]);

    const statesOptions = useMemo(() => {
        return statesSetting ? statesSetting.map((item: any) => ({
            label: item.content,
            value: item.dictKey,
        })) : [];
    }, [statesSetting]);

    const statesFilterOptions = useMemo(() => {
        return statesSetting ? statesSetting.map((item: any) => ({
            text: item.content,
            value: item.dictKey,
        })) : [];
    }, [statesSetting]);

    const fetchData = useCallback(async (params: IGetClosedFunnelListInput) => {
        setLoading(true);
        try {
            const res = await getClosedFunnelList(params);
            if (res && !res.error) {
                setData(res?.data?.data?.list || []);
                setTotal(res?.data?.data?.totalRecords || 0);
            } else {
                message.error(res?.error);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleGetClosedFunnelLink = useCallback(async (id: number) => {
        try {
            setCurrentLoadingRowId(id);
            const res = await getClosedFunnelLink(id);
            if (res && !res.error) {
                if (res?.data?.data) {
                    window.open(res?.data?.data, '_blank');
                } else {
                    message.error('No link found. Please try again.');
                }
            } else {
                message.error(res?.error);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setCurrentLoadingRowId(null);
        }
    }, []);

    const handleUpdateClosedFunnelStatus = useCallback(async (id: number, status: 'A' | 'D') => {
        try {
            setCurrentLoadingRowIdStatus(id);
            const res = await updateClisedFunnelStatus(id, status);
            if (res && !res.error) {
                fetchData(searchParams.current);
            } else {
                message.error(res?.error);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setCurrentLoadingRowIdStatus(null);
        }
    }, [fetchData]);

    const handleDuplicateClosedFunnel = useCallback(async (id: number) => {
        try {
            setLoading(true);
            const res = await duplicateClosedFunnel(id);
            if (res && !res.error) {
                fetchData(searchParams.current);
            } else {
                message.error(res?.error);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [fetchData]);

    const handleViewClosedFunnel = useCallback(async (id: number) => {
        navigate(`${PATH.OPERATION}/${PATH.CLOSED_FUNNEL_DETAIL}/${id}`);
    }, [navigate]);

    const filterNameDebounce = useRef(debounce((name: string) => {
        searchParams.current.name = name;
        searchParams.current.current = 1;
        fetchData(searchParams.current);
    }, 500));

    useEffect(() => {
        fetchData({
            current: 1,
            size: 10,
            name: '',
            states: [],
            type: 'CONDITION',
            status: [],
            typeValues: [],
            orderType: 'DESC',
        });
    }, [fetchData]);

    const handleTableChange = useCallback((paginationData?:any, filtersData?:any, sorter?:any, action?:any) => {
        const params = searchParams.current;

        if (action?.action === 'sort') {
            params.orderType = sorter.order === 'ascend' ? 'ASC' : 'DESC';
        }

        params.name = pendingNameFilter;
        params.states = filtersData.states && filtersData.states.length > 0 ? filtersData.states : null;
        params.type = filtersData.type && filtersData.type.length > 0 ? filtersData.type.join(',') : null;
        params.status = filtersData.status;
        params.typeValues = filtersData.typeValues && filtersData.typeValues.length > 0 ? filtersData.typeValues : null;
        params.current = paginationData.current;
        params.size = paginationData.pageSize;
        fetchData(params);
    }, [fetchData, pendingNameFilter]);

    const columns = useMemo(() => {
        return [
            {
                title: 'FUNNEL NAME',
                dataIndex: 'name',
                key: 'name',
                width: 200,
                filterDropdown: () => (
                    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                        <Input
                            value={pendingNameFilter}
                            onChange={(e) => {
                                const filterName = e.target.value;
                                setPendingNameFilter(filterName);
                                filterNameDebounce.current(filterName);
                            }}
                            style={{ marginBottom: 8, display: 'block' }}
                        />
                    </div>
                ),
                filterIcon: <SearchOutlined />,
                render: (text: string) => (
                    <Tooltip title={text}>
                        <span className={styles.funnelNameEllipsis}>{text}</span>
                    </Tooltip>
                ),
            },
            {
                title: 'APPLICABLE STATES',
                dataIndex: 'states',
                key: 'states',
                width: 250,
                filterMultiple: true,
                filters: statesFilterOptions,
                render: (text: string) => {
                    const valueList = text.split(',');
                    const labelList = valueList.map((value) => statesOptions.find((item) => item.value.toLowerCase() === value.toLowerCase())?.label);
                    const valueText = labelList.join(', ');
                    return <Tooltip title={valueText}><span className={styles.funnelNameEllipsis}>{valueText}</span></Tooltip>;
                },
            },
            {
                title: 'TYPE',
                dataIndex: 'type',
                key: 'type',
                width: 250,
                filters: [
                    {
                        text: 'Condition',
                        value: 'CONDITION',
                    },
                    // {
                    //     text: 'Medication',
                    //     value: 'MEDICATION',
                    // },
                ],
                render: (text: string) => {
                    return capitalize(text);
                },
            },
            {
                title: 'STATUS',
                dataIndex: 'status',
                key: 'status',
                width: 150,
                filters: [
                    {
                        text: 'Actived',
                        value: 'A',
                    },
                    {
                        text: 'De-active',
                        value: 'D',
                    },
                ],
                render: (text: string) => {
                    return text === 'A' ? 'Active' : 'De-active';
                },
            },
            {
                title: 'CONDITION',
                dataIndex: 'typeValues',
                key: 'typeValues',
                filters: conditionFilterOptions,
                width: 250,
                render: (text: string) => {
                    return <Tooltip title={text}><span className={styles.funnelNameEllipsis}>{text}</span></Tooltip>;
                },
            },
            {
                title: 'LAST MODIFIED',
                dataIndex: 'updatedAt',
                key: 'updatedAt',
                width: 150,
                sorter: true,
                render: (text: string) => {
                    return text ? dayjs(text).format('MM/DD/YYYY') : '';
                },
            },
            {
                title: '',
                key: 'statusAction',
                width: 120,
                render: (record: ICloseFunnelRow) => (
                    <Button
                        onClick={() => {
                            handleUpdateClosedFunnelStatus(record.id, record.status === 'A' ? 'D' : 'A');
                        }}
                        loading={currentLoadingRowIdStatus === record.id}
                        style={{ color: '#009872' }}
                        type="link"
                    >
                        {record.status === 'A' ? 'De-active' : 'Active'}
                    </Button>
                ),
                fixed: 'right',
            },
            {
                title: '',
                key: 'duplicateAction',
                width: 120,
                render: (record: ICloseFunnelRow) => (
                    <Button
                        onClick={() => {
                            handleDuplicateClosedFunnel(record.id);
                        }}
                        type="link"
                        style={{ color: '#009872' }}
                    >
                        Duplicate
                    </Button>
                ),
                fixed: 'right',
            },
            {
                title: '',
                key: 'linkAction',
                width: 150,
                render: (record: ICloseFunnelRow) => (
                    <Button
                        onClick={() => {
                            handleGetClosedFunnelLink(record.id);
                        }}
                        loading={currentLoadingRowId === record.id}
                        type="link"
                        style={{ color: '#009872' }}
                    >
                        <span className={styles.iconButton}>Link <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none"><path d="M7.08398 4.7487H4.25065C3.46825 4.7487 2.83398 5.38296 2.83398 6.16536V13.2487C2.83398 14.0311 3.46825 14.6654 4.25065 14.6654H11.334C12.1164 14.6654 12.7507 14.0311 12.7507 13.2487V10.4154M9.91732 3.33203H14.1673M14.1673 3.33203V7.58203M14.1673 3.33203L7.08398 10.4154" stroke="#00816B" strokeWidth="1.41667" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                    </Button>
                ),
                fixed: 'right',
            },
            {
                title: '',
                key: 'viewAction',
                width: 100,
                render: (record: ICloseFunnelRow) => (
                    <span onClick={() => handleViewClosedFunnel(record.id)} style={{ color: '#009872', fontWeight: 500, cursor: 'pointer' }}>View</span>
                ),
                fixed: 'right',
            },
        ];
    }, [conditionFilterOptions, currentLoadingRowId, currentLoadingRowIdStatus, handleDuplicateClosedFunnel, handleGetClosedFunnelLink, handleUpdateClosedFunnelStatus, handleViewClosedFunnel, pendingNameFilter, statesFilterOptions, statesOptions]);

    return (
        <div className={styles.closedFunnelPage}>
            <AddModal
                open={openAddModal}
                statesOptions={statesOptions}
                onCancel={() => setOpenAddModal(false)}
                onAdd={(id) => {
                    // fetchData(searchParams.current);
                    setOpenAddModal(false);
                    handleViewClosedFunnel(id);
                }}
                conditionOptions={conditionOptions}
            />
            <div className={styles.header}>
                <span className={styles.title}>Closed funnel</span>
                <Button className={styles.addBtn} icon={<PlusOutlined />} onClick={() => setOpenAddModal(true)}> Add a new funnel</Button>
            </div>
            <CommonTable
                className={styles.table}
                columns={columns}
                locale={{ emptyText: 'No funnel yet' }}
                rowKey="id"
                data={data}
                pagination={{
                    pageSize: searchParams.current.size,
                    current: searchParams.current.current,
                    total,
                }}
                scroll={{ x: 'max-content' }}
                onChange={handleTableChange}
                loading={loading}
            />
        </div>
    );
};

export default ClosedFunnel;
